import React, { useMemo, useState, useCallback } from 'react';
import { Button, XStack, YStack, useTheme, Sheet } from 'tamagui';

import {
  CobuStack,
  CobuText,
  CobuSimpleAvatar,
} from '@cobu/ui';
import { useEmojiPalette } from '../../../features/reactions/context/EmojiProvider';
import { CommentType, CommentReaction } from '@cobu/types';
import { useCommentReactionsQuery } from '@cobu/api-client';
import { DEFAULT_REACTIONS, ReactionType } from '../../../hooks/useReactions';
import { URLS } from '../../../constants';
import { Strings } from '../../../constants/Strings';



interface ReactionsModalProps {
  visible: boolean;
  onClose: () => void;
  commentId: string | null;
  commentType?: CommentType;
}

const UNDERLINE_HEIGHT = 3;
const EMOJI_SIZE = 16;

const ReactionsModal: React.FC<ReactionsModalProps> = ({
  visible,
  onClose,
  commentId,
  commentType = CommentType.GroupComment,
}) => {
  const theme = useTheme();
  const { byTitle } = useEmojiPalette();
  const reactionStrings = Strings.get().discussion.reactions;

  const { data: reactions = [], isLoading } = useCommentReactionsQuery(
    commentId || '',
    commentType,
    visible && !!commentId
  );

  const [filter, setFilter] = useState<string | null>(null);
  const [headerWidths, setHeaderWidths] = useState<Record<string, number>>({});
  const setWidth = useCallback((key: string) => (e: any) => {
    const w = Math.round(e.nativeEvent?.layout?.width || 0);
    setHeaderWidths(prev => (prev[key] === w ? prev : { ...prev, [key]: w }));
  }, []);

  const getReactionKey = useCallback((r: any) => {
    const raw = (r?.reactionName ?? r?.name ?? '').toString();
    return raw.toLowerCase();
  }, []);

  const grouped = useMemo(() => {
    const by: Record<string, CommentReaction[]> = {} as any;
    reactions.forEach((r: any) => {
      const key = getReactionKey(r);
      if (!key) return;
      if (!by[key]) by[key] = [] as any;
      by[key].push(r);
    });
    return by;
  }, [reactions, getReactionKey]);

  const sortedKeys = useMemo(() => {
    return Object.keys(grouped).sort((a, b) => (grouped[b]?.length || 0) - (grouped[a]?.length || 0));
  }, [grouped]);

  const headerUnderlineColor = (reactionKey?: string) => {
    const active = reactionKey ? filter === reactionKey : !filter;
    if (!reactionKey) {
      return '$black';
    }

    const paletteItem = byTitle[reactionKey];
    const fallback = DEFAULT_REACTIONS.find(r => r.type === (reactionKey as ReactionType));
    const colorCandidate = (paletteItem?.meta?.colorScheme as string) || fallback?.color || '';
    const color = '$black';

    return active ? color : 'rgba(39,33,99,0.05)';
  };

  const renderEmoji = (reactionKey: string) => {
    const key = (reactionKey || '').toLowerCase();
    const emojiItem = byTitle[key];
    const fallback = DEFAULT_REACTIONS.find(r => r.type === (key as ReactionType));
    if (emojiItem?.url) {
      return (
        <img src={emojiItem.url} style={{ width: EMOJI_SIZE, height: EMOJI_SIZE }} />
      );
    }
    const text = emojiItem?.text || fallback?.emoji;
    if (!text) return null;
    return (
      <CobuText fontSize={16}>{text}</CobuText>
    );
  };

  const filteredReactions = useMemo(() => {
    if (!filter) return reactions as any[];
    return (reactions as any[]).filter(r => getReactionKey(r) === filter);
  }, [reactions, filter, getReactionKey]);

  const renderShimmer = () => (
    <YStack gap="$2" px="$4" py="$3">
      {Array.from({ length: 6 }).map((_, idx) => (
        <XStack key={idx} ai="center" gap="$3">
          <YStack w="$6" h="$6" br={24} bg="$backgroundGrey" />
          <YStack f={1} gap="$1">
            <YStack w="50%" h="$2" br="$2" bg="$backgroundGrey" />
            <YStack w="30%" h="$2" br="$2" bg="$backgroundGrey" />
          </YStack>
        </XStack>
      ))}
    </YStack>
  );

  return (
    <Sheet
      open={visible}
      modal
      onOpenChange={(next: boolean) => {
        if (!next) {
          setFilter(null);
          onClose();
        }
      }}
      snapPoints={[40, 40, 40]}
      snapPointsMode="percent"
      dismissOnSnapToBottom
      animation={undefined as any}
      zIndex={100_000}
      forceRemoveScrollEnabled={visible}
    >
      <Sheet.Overlay
        animation={undefined as any}
        enterStyle={{ opacity: 0 }}
        exitStyle={{ opacity: 0 }}
        bg={theme.blackTransparent50?.val}
      />
  
      <Sheet.Frame padding="$4" gap="$3" bc="white">
        {/* Header: filters */}
        <CobuStack gap="$2">
          <XStack ai="center" gap="$4" jc="flex-start" flexWrap="wrap">
            <Button
              unstyled
              onPress={() => setFilter(null)}
              accessibilityRole="button"
            >
              <YStack>
                <CobuText
                  variant="body"
                  // onLayout={setWidth('all')}
                >
                  {reactionStrings.allReactions} {reactions.length}
                </CobuText>
                <YStack
                  h={UNDERLINE_HEIGHT}
                  w={headerWidths['all'] || 0}
                  bg={headerUnderlineColor() as any}
                  br="$2"
                  mt="$1.5"
                />
              </YStack>
            </Button>
  
            {sortedKeys.map((key) => (
              <Button
                key={key}
                unstyled
                onPress={() => setFilter(key)}
                accessibilityRole="button"
              >
                <YStack>
                  <XStack ai="center" gap="$2" onLayout={setWidth(key)}>
                    {renderEmoji(key)}
                    <CobuText>{grouped[key].length}</CobuText>
                  </XStack>
                  <YStack
                    h={UNDERLINE_HEIGHT}
                    w={headerWidths[key] || 0}
                    bg={headerUnderlineColor(key) as any}
                    br="$2"
                    mt="$1.5"
                  />
                </YStack>
              </Button>
            ))}
          </XStack>
        </CobuStack>
  
        {/* Body */}
        {isLoading ? (
          renderShimmer()
        ) : (
          <Sheet.ScrollView>
            <YStack px="$1" pb="$6" pt="$2" gap="$1">
              {filteredReactions.map((r: any) => (
                <XStack key={r.uuid} ai="center" py="$3" gap="$3">
                  <YStack pos="relative">
                    {r.user.profileImage && r.user.profileImage !== URLS.DEFAULT_PROFILE_IMAGE ? (
                      // inline avatar to avoid external StyleSheet dep
                      <YStack
                        w={40}
                        h={40}
                        br={20}
                        overflow="hidden"
                      >
                        <img
                          src={r.user.profileImage}
                          style={{ width: 40, height: 40, objectFit: 'cover' }}
                        />
                      </YStack>
                    ) : (
                      <CobuSimpleAvatar user={r.user} size={40} fontSize={16} />
                    )}
  
                    <YStack pos="absolute" b={0} r={-EMOJI_SIZE / 2}>
                      {renderEmoji(getReactionKey(r))}
                    </YStack>
                  </YStack>
  
                  <CobuText variant="body">
                    {r.user.displayName}
                  </CobuText>
                </XStack>
              ))}
            </YStack>
          </Sheet.ScrollView>
        )}
      </Sheet.Frame>
    </Sheet>
  );
  
};

export default ReactionsModal; 